import type { Middleware } from 'openapi-fetch';
import { ConflictError, type ConflictKind } from './errors';

/**
 * Optimistic concurrency, client half (architecture §B.10 / decision T-12).
 *
 * The server side of the contract is: every mutating request must quote the ETag it read, as
 * `If-Match`. Missing → 428, stale → 412. Making every call site remember that would guarantee
 * that some call site forgets, so the SDK does it: GET responses park their ETag, mutations
 * take it back out, and the two failure codes come back as one typed error.
 */

/** Per-client, in memory, and deliberately not shared: two clients are two beliefs. */
export interface EtagStore {
  get: (url: string) => string | undefined;
  set: (url: string, etag: string) => void;
  forget: (url: string) => void;
  clear: () => void;
}

export function createEtagStore(): EtagStore {
  const etags = new Map<string, string>();
  return {
    get: (url) => etags.get(url),
    set: (url, etag) => {
      etags.set(url, etag);
    },
    forget: (url) => {
      etags.delete(url);
    },
    clear: () => {
      etags.clear();
    },
  };
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const CONFLICT_KINDS = new Map<number, ConflictKind>([
  [428, 'precondition-required'],
  [412, 'precondition-failed'],
  [409, 'conflict'],
]);

/**
 * Reasons that arrive as 409 and are NOT concurrency conflicts.
 *
 * A ConflictError means one thing to every caller: the belief you acted on is stale, so re-read
 * and let the user decide. `REF_CONFLICT` — a manifest pinned something that does not exist, is
 * not published, or belongs to another product (service summary §5.1) — is a 409 that re-reading
 * cannot fix, because nothing about the resource is out of date. Surfacing it as a conflict would
 * make the console offer "the data changed, reload" for a manifest that will be refused exactly
 * the same way after the reload.
 *
 * `ASSET_NOT_EDITABLE` (SDD-010 R-14) is the same kind of answer: the asset is PUBLISHED or
 * DISCARDED, so no re-read makes it writable again — the fix is a new draft, and the console has
 * to be able to say so instead of "the data changed, reloaded".
 *
 * The lead refusals (SDD-013 §7, `LeadConflictReasons` in pkg/svccontract) are the same kind of
 * answer again: the lead is locked, not yet accepted, on a stale pin, asked for a transition its
 * machine does not have — states a re-read does not change, and reasons the console renders.
 *
 * Every reason here is platform contract (pkg/svccontract), not one service's vocabulary, which is
 * why the runtime is allowed to know them.
 */
const NON_CONCURRENCY_409 = new Set([
  'REF_CONFLICT',
  'ASSET_NOT_EDITABLE',
  // The lead refusals (SDD-013 §7, pkg/svccontract `LeadConflictReasons`): the lead is not in a
  // state where the operation means anything — locked, not yet accepted, a transition the pinned
  // machine does not have, a draft partner still unregistered, a stale pin. Nothing moved, the
  // parked ETag stays valid, and a console renders the REASON (two prices for a stale pin) rather
  // than "data changed, reloaded". PARTNER_DRAFT_INVALID (SDD-014 PS-5) is the Partner domain
  // refusing a draft at accept — a fixed refusal a re-read cannot change.
  'LEAD_LOCKED',
  'LEAD_NOT_ACCEPTED',
  'LEAD_ALREADY_CONVERTED',
  'LEAD_PIN_STALE',
  'LEAD_EXPIRED',
  'TRANSITION_NOT_ALLOWED',
  'TRANSITION_REQUIRES_APPROVE',
  'TRANSITION_REASON_REQUIRED',
  'FOUR_EYES',
  'PARTNER_DRAFT_INVALID',
  'PARTNER_ROLE_DUPLICATE',
  // The billing refusals (SDD-015 §6.8, pkg/svccontract `BillingConflictReasons`): the period is
  // already invoiced, the invoice cannot be reverted (an estimate, a refunded one, a reversal), a
  // run of that kind is in progress, there is nothing to bill, the policy is billed through its
  // master, the currencies differ. Nothing moved and no re-read changes the answer — the console
  // renders the REASON ("this period is already invoiced"), never "data changed, reloaded".
  'PERIOD_ALREADY_INVOICED',
  'INVOICE_NOT_REVERTIBLE',
  'RUN_IN_PROGRESS',
  'NOTHING_TO_BILL',
  'NO_SCHEDULE',
  'CURRENCY_MISMATCH',
  // An object-only member cannot be detached: it carries no insured person to become its
  // policyholder (SDD-022 D-3, OQ-57, pkg/svccontract). Nothing moved; the reason is the answer.
  'MEMBER_HAS_NO_POLICYHOLDER_CANDIDATE',
]);

/** The machine-readable reason in the gateway's error envelope, or undefined. */
function reasonOf(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const details: unknown = (body as { details?: unknown }).details;
  if (!Array.isArray(details)) return undefined;
  for (const detail of details) {
    if (typeof detail !== 'object' || detail === null) continue;
    const type: unknown = (detail as Record<string, unknown>)['@type'];
    if (typeof type !== 'string' || !type.endsWith('google.rpc.ErrorInfo')) continue;
    const reason: unknown = (detail as { reason?: unknown }).reason;
    if (typeof reason === 'string') return reason;
  }
  return undefined;
}

/** Query strings identify a view of a resource, never the resource. */
function resourceKey(url: string): string {
  const parsed = new URL(url);
  return `${parsed.origin}${parsed.pathname}`;
}

/**
 * The URLs a mutation may inherit an ETag from, nearest first.
 *
 * The gateway maps state transitions to POST actions rather than PATCHed status fields
 * (architecture §B.7), so the thing being guarded is almost never the URL being posted to:
 * `POST /v1/policies/P-1:activate` guards `/v1/policies/P-1`, and
 * `POST /v1/policies/P-1/endorsements` guards the same. Both are reached by dropping the
 * custom-method suffix and then walking up, stopping at the version prefix — one path shape,
 * derived rather than configured, because a hand-maintained table of "which URL guards which"
 * is a table that goes stale on the next endpoint.
 */
export function guardedResourceUrls(url: string): string[] {
  const parsed = new URL(url);
  const candidates: string[] = [];
  const segments = parsed.pathname.split('/').filter((segment) => segment !== '');

  // `versions:publish` → also consider `versions`.
  const last = segments.at(-1);
  if (last?.includes(':') === true) {
    segments[segments.length - 1] = last.slice(0, last.indexOf(':'));
  }

  // Walk up to, but not past, the API version prefix: `/v1` alone identifies no resource.
  for (let depth = segments.length; depth > 1; depth -= 1) {
    candidates.push(`${parsed.origin}/${segments.slice(0, depth).join('/')}`);
  }
  return candidates;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.clone().text();
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * Captures ETags from safe responses, injects `If-Match` on mutations, and turns
 * 428/412/409 into {@link ConflictError}. An `If-Match` the caller set explicitly always wins:
 * a hook that has just re-read a resource knows better than the store.
 */
export function createEtagMiddleware(store: EtagStore): Middleware {
  return {
    onRequest({ request }) {
      if (SAFE_METHODS.has(request.method)) return undefined;
      if (request.headers.has('If-Match')) return undefined;

      for (const candidate of guardedResourceUrls(request.url)) {
        const etag = store.get(candidate);
        if (etag !== undefined) {
          // Headers are mutated in place rather than through `new Request(request)`, which would
          // consume the body stream of a mutation that has one.
          request.headers.set('If-Match', etag);
          return request;
        }
      }
      return undefined;
    },

    async onResponse({ request, response }) {
      const etag = response.headers.get('ETag');
      const key = resourceKey(request.url);

      if (SAFE_METHODS.has(request.method)) {
        if (etag !== null && response.ok) store.set(key, etag);
        return undefined;
      }

      const kind = CONFLICT_KINDS.get(response.status);
      if (kind !== undefined) {
        const body = await readBody(response);
        // A 409 that names a non-concurrency reason is an ordinary error: it passes through to
        // the caller's result, and the parked ETag stays valid because nothing moved.
        if (kind === 'conflict' && NON_CONCURRENCY_409.has(reasonOf(body) ?? '')) return undefined;

        // The belief that produced this request is void either way; the next read re-establishes it.
        for (const candidate of guardedResourceUrls(request.url)) store.forget(candidate);
        throw new ConflictError({
          kind,
          status: response.status,
          method: request.method,
          url: request.url,
          resourceUrl: guardedResourceUrls(request.url)[0] ?? key,
          sentEtag: request.headers.get('If-Match') ?? undefined,
          currentEtag: etag ?? undefined,
          body,
        });
      }

      if (response.ok) {
        // A successful write moved the resource: the old ETag is now a guaranteed 412. The new
        // one is deliberately NOT captured — a create posts to the collection, so the ETag on its
        // response belongs to a resource whose URL this middleware cannot know, and guessing
        // would let one resource's ETag guard another's. The next read establishes the belief.
        for (const candidate of guardedResourceUrls(request.url)) store.forget(candidate);
      }
      return undefined;
    },
  };
}
